// 侧栏审核角标数据 hook（US-002 + FIX-001 + FIX-003）：
// 「入金审核」「提现审核」「代理商资质审核」「商户开户审核」角标真实化。
// 入金/提现状态判断一律复用 finance.ts 既有映射函数；
// 代理/商户开户待审口径与对应 Audit 页 KPI 同源：
//   - 代理：rawStatus === PENDING_APPROVAL
//   - 商户：status === 'PENDING'（mapAuditStatus 后，与 MerchantOnboardingAudit stats.pending 同源）
import { useEffect, useRef, useState } from 'react';
import {
  isReviewableWithdrawalStatus,
  listDeposits,
  listWithdrawals,
  mapDepositStatus,
} from '@/api/finance';
import {
  listAgentOnboardingApplications,
  listMerchantOnboardingApplications,
  toOrigOnboarding,
} from '@/api/onboarding';
import {
  playReviewSound,
  shouldAlertReviewIncrease,
  sumReviewCounts,
  type ReviewBadgeCounts,
} from '@/lib/reviewSound';

/** 待办轮询间隔：新审批到达后 60s 内响提示音（含侧栏角标同步刷新） */
const REVIEW_POLL_MS = 60_000;

/** 审核角标计数：null = 未加载 / 加载失败 → 不显示角标（静默降级，不阻塞侧栏渲染） */
export type ReviewBadges = {
  /** 待审入金数：listDeposits() → mapDepositStatus 映射为 pending 的计数（与 DepositReview 页 KPI 同源同窗口） */
  depositPendingCount: number | null;
  /** 审中提现数：listWithdrawals() 全量 → isReviewableWithdrawalStatus 过滤计数 */
  withdrawalReviewableCount: number | null;
  /**
   * 代理商开户待审数：listAgentOnboardingApplications() → toOrigOnboarding →
   * rawStatus === 'PENDING_APPROVAL' 计数（与 AgentOnboardingAudit 页「待审核」KPI 同源）
   */
  agentOnboardingPendingCount: number | null;
  /**
   * 商户开户待审数：listMerchantOnboardingApplications() → toOrigOnboarding →
   * status === 'PENDING' 计数（与 MerchantOnboardingAudit stats.pending 同源）
   */
  merchantOnboardingPendingCount: number | null;
};

/**
 * 挂载时拉取一次 + pathname 变化时重拉（用户在审核页完成通过/拒绝并离开后，
 * 角标随路由切换刷新，不残留处理前旧值）。
 * 各链路独立降级：任一请求失败仅置对应计数为 null，不影响其他条。
 */
export function useReviewBadges(pathname: string): ReviewBadges {
  const [depositPendingCount, setDepositPendingCount] = useState<number | null>(null);
  const [withdrawalReviewableCount, setWithdrawalReviewableCount] = useState<number | null>(null);
  const [agentOnboardingPendingCount, setAgentOnboardingPendingCount] = useState<number | null>(
    null,
  );
  const [merchantOnboardingPendingCount, setMerchantOnboardingPendingCount] = useState<
    number | null
  >(null);
  /** 上次「全部链路可信」的待办总数；null = 尚无可信快照（不响） */
  const lastTrustedTotalRef = useRef<number | null>(null);

  useEffect(() => {
    // 竞态保护：pathname 快速切换/轮询重疋时，已被取代的旧请求返回后不得覆盖新状态
    let stale = false;

    const loadDeposits = async () => {
      try {
        const res = await listDeposits();
        if (stale) return;
        if (!res.ok) {
          setDepositPendingCount(null);
          return;
        }
        setDepositPendingCount(
          res.data.items.filter((d) => mapDepositStatus(d.status) === 'pending').length,
        );
      } catch {
        if (!stale) setDepositPendingCount(null);
      }
    };

    const loadWithdrawals = async () => {
      try {
        const res = await listWithdrawals();
        if (stale) return;
        if (!res.ok) {
          setWithdrawalReviewableCount(null);
          return;
        }
        setWithdrawalReviewableCount(
          res.data.items.filter((w) => isReviewableWithdrawalStatus(w.status)).length,
        );
      } catch {
        if (!stale) setWithdrawalReviewableCount(null);
      }
    };

    const loadAgentOnboarding = async () => {
      try {
        const res = await listAgentOnboardingApplications();
        if (stale) return;
        if (!res.ok) {
          setAgentOnboardingPendingCount(null);
          return;
        }
        // 与 AgentOnboardingAudit stats.pending 同口径：仅 PENDING_APPROVAL
        const pending = res.data.items
          .map(toOrigOnboarding)
          .filter((r) => r.rawStatus === 'PENDING_APPROVAL').length;
        setAgentOnboardingPendingCount(pending);
      } catch {
        if (!stale) setAgentOnboardingPendingCount(null);
      }
    };

    const loadMerchantOnboarding = async () => {
      try {
        const res = await listMerchantOnboardingApplications();
        if (stale) return;
        if (!res.ok) {
          setMerchantOnboardingPendingCount(null);
          return;
        }
        // 与 MerchantOnboardingAudit stats.pending 同口径：mapAuditStatus 后 status === PENDING
        const pending = res.data.items
          .map(toOrigOnboarding)
          .filter((r) => r.status === 'PENDING').length;
        setMerchantOnboardingPendingCount(pending);
      } catch {
        if (!stale) setMerchantOnboardingPendingCount(null);
      }
    };

    const loadAll = () => {
      void loadDeposits();
      void loadWithdrawals();
      void loadAgentOnboarding();
      void loadMerchantOnboarding();
    };

    loadAll();
    // 新审批提示音依赖轮询感知新待办；同时让角标不再只在路由切换时刷新
    const timer = window.setInterval(loadAll, REVIEW_POLL_MS);

    return () => {
      stale = true;
      window.clearInterval(timer);
    };
  }, [pathname]);

  // 新审批提示音：四链计数全部可信且总数较上次可信快照增加 → 响铃
  useEffect(() => {
    const counts: ReviewBadgeCounts = {
      depositPendingCount,
      withdrawalReviewableCount,
      agentOnboardingPendingCount,
      merchantOnboardingPendingCount,
    };
    const total = sumReviewCounts(counts);
    if (shouldAlertReviewIncrease(lastTrustedTotalRef.current, total)) {
      playReviewSound();
    }
    if (total != null) {
      lastTrustedTotalRef.current = total;
    }
  }, [depositPendingCount, withdrawalReviewableCount, agentOnboardingPendingCount, merchantOnboardingPendingCount]);

  return {
    depositPendingCount,
    withdrawalReviewableCount,
    agentOnboardingPendingCount,
    merchantOnboardingPendingCount,
  };
}
